import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Form } from "react-bootstrap";
import QuickQuizService from "../../_services/QuickQuizService";

interface Choice {
  choiceString: string;
  name: string;
  correctFlag: number;
}

interface TestQuestion {
  mappingId: number;
  sectionId: number;
  sectionName: string;
  questionId: number;
  questionNumber: number;
  testQuestionNumber: number;
  questionName: string;
  stemStatement: string;
  questionCategory: number;
  marks: number;
  negativeMarks: number;
  /** -1 = DEFAULT, 1 = PARTIAL, 2 = ONE-OF */
  markingScheme: number;
  bonusMark: boolean;
  /** Integer (cat 4) answer key. Numeric/text categories store the answer here, not in choices. */
  answer: string;
  /** Integer Range (cat 5) bounds. */
  answerRangeStart: number | null;
  answerRangeEnd: number | null;
  choices: Choice[];
}

/**
 * Marking scheme dropdown — codes match Java TestSectionQuestionMapping.Constants.
 */
const MARKING_SCHEME_OPTIONS: Array<{ value: number; label: string }> = [
  { value: -1, label: "DEFAULT" },
  { value: 2, label: "ONE-OF" },
  { value: 1, label: "PARTIAL" },
];

/** Integer (4) / Integer Range (5) questions support a scheme but have no choices. */
const INTEGER_CATEGORIES = [4, 5];

/**
 * Whether a marking scheme applies to this question — the categories Java's
 * computeMarksBasedOnMarkingScheme handles: choice-based questions (MCQ, SCQ,
 * which expose `choices`) and integer types. Robust to the exact category code.
 */
function supportsMarkingScheme(q: TestQuestion): boolean {
  return q.choices.length > 0 || INTEGER_CATEGORIES.includes(q.questionCategory);
}

interface TestQuestionsData {
  test: { testId: number; name: string; totalQuestions: number };
  questions: TestQuestion[];
}

interface TestQuestionsPanelProps {
  testId: number;
  onBack: () => void;
  onViewTest?: (testId: number) => void;
}

const TestQuestionsPanel: React.FC<TestQuestionsPanelProps> = ({ testId, onBack, onViewTest }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TestQuestionsData | null>(null);

  const [editState, setEditState] = useState<Record<number, {
    marks: string;
    negativeMarks: string;
    markingScheme: string;
    bonusMark: boolean;
    correctChoices: Set<string>;
    answer: string;
    answerRangeStart: string;
    answerRangeEnd: string;
  }>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await QuickQuizService.getTestQuestions(testId);
      if (result.success) {
        setData(result.data);
        const state: typeof editState = {};
        for (const q of result.data.questions) {
          state[q.mappingId] = {
            marks: String(q.marks ?? ""),
            negativeMarks: String(q.negativeMarks ?? 0),
            markingScheme: String(q.markingScheme ?? -1),
            bonusMark: q.bonusMark,
            correctChoices: new Set(q.choices.filter(c => c.correctFlag === 1).map(c => c.choiceString)),
            answer: q.answer ?? "",
            answerRangeStart: q.answerRangeStart == null ? "" : String(q.answerRangeStart),
            answerRangeEnd: q.answerRangeEnd == null ? "" : String(q.answerRangeEnd),
          };
        }
        setEditState(state);
        setError(null);
      } else {
        setError("Failed to load test questions");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to load test questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdate = async (q: TestQuestion) => {
    const es = editState[q.mappingId];
    if (!es) return;
    setSavingId(q.mappingId);
    try {
      const result = await QuickQuizService.updateTestQuestion(testId, q.mappingId, {
        marks: Number(es.marks),
        negativeMarks: Number(es.negativeMarks),
        // Only choice-based / integer questions carry a marking scheme (matches Java)
        ...(supportsMarkingScheme(q)
          ? { markingScheme: Number(es.markingScheme) }
          : {}),
        bonusMark: es.bonusMark,
        correctChoices: Array.from(es.correctChoices),
        // Numeric answer key for Integer (4) / Integer Range (5) questions.
        ...(q.questionCategory === 4 ? { answer: es.answer } : {}),
        ...(q.questionCategory === 5
          ? {
              answerRangeStart: es.answerRangeStart === "" ? null : Number(es.answerRangeStart),
              answerRangeEnd: es.answerRangeEnd === "" ? null : Number(es.answerRangeEnd),
            }
          : {}),
      });
      alert(result.message || "Updated successfully");
    } catch (err: any) {
      alert(err?.response?.data?.message || err.message || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const toggleChoice = (mappingId: number, choiceString: string) => {
    setEditState(prev => {
      const current = prev[mappingId];
      if (!current) return prev;
      const newChoices = new Set(current.correctChoices);
      if (newChoices.has(choiceString)) newChoices.delete(choiceString);
      else newChoices.add(choiceString);
      return { ...prev, [mappingId]: { ...current, correctChoices: newChoices } };
    });
  };

  const setField = (mappingId: number, field: "marks" | "negativeMarks" | "markingScheme" | "answer" | "answerRangeStart" | "answerRangeEnd", value: string) => {
    setEditState(prev => {
      const current = prev[mappingId];
      if (!current) return prev;
      return { ...prev, [mappingId]: { ...current, [field]: value } };
    });
  };

  const toggleBonus = (mappingId: number) => {
    setEditState(prev => {
      const current = prev[mappingId];
      if (!current) return prev;
      return { ...prev, [mappingId]: { ...current, bonusMark: !current.bonusMark } };
    });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <div style={{ textAlign: "center" }}>
          <Spinner role="status" animation="border" style={{ width: 40, height: 40, color: "#5b6abf" }} />
          <div style={{ marginTop: 12, color: "var(--text-muted)", fontSize: 14, fontWeight: 500 }}>Loading questions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card style={{ border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
        <Card.Body>
          <div style={{ color: "var(--status-danger)", marginBottom: 12, fontWeight: 500 }}>{error}</div>
          <Button size="sm" onClick={onBack}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", borderRadius: 6, padding: "6px 16px", margin: "2px" }}>
            Back
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div>
      {/* Top toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" onClick={onBack}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", borderRadius: 6, padding: "6px 16px", margin: "2px", fontSize: 13, fontWeight: 500 }}>
            Back
          </Button>
          {onViewTest && (
            <Button size="sm" onClick={() => onViewTest(testId)}
              style={{ backgroundColor: "#5b6abf", color: "var(--text-on-brand)", border: "none", borderRadius: 6, padding: "6px 16px", margin: "2px", fontSize: 13, fontWeight: 500 }}>
              View Test
            </Button>
          )}
        </div>
        <span style={{
          display: "inline-block", background: "#e8ecf4", color: "var(--text-body)", fontSize: 12, fontWeight: 600,
          padding: "4px 12px", borderRadius: 20, letterSpacing: 0.3,
        }}>
          {data.test.totalQuestions} Questions
        </span>
      </div>

      {/* Test header */}
      <div style={{
        background: "linear-gradient(135deg, #5b6abf 0%, var(--brand-accent) 100%)",
        color: "var(--text-on-brand)", borderRadius: 10, padding: "18px 24px", marginBottom: 20,
        boxShadow: "0 4px 14px rgba(91,106,191,0.3)",
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, opacity: 0.8 }}>Test Name</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginTop: 2 }}>{data.test.name}</div>
      </div>

      {/* Questions */}
      {data.questions.map((q, idx) => {
        const es = editState[q.mappingId];
        const isSaving = savingId === q.mappingId;

        return (
          <div key={q.mappingId} style={{
            background: "var(--surface-card)", borderRadius: 10, marginBottom: 14,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #e8ecf0",
            overflow: "hidden", transition: "box-shadow 0.2s",
          }}>
            {/* Question number header */}
            <div style={{
              background: idx % 2 === 0
                ? "linear-gradient(90deg, #e8ecf4 0%, #f1f3f9 100%)"
                : "linear-gradient(90deg, #eef0f8 0%, #f5f6fb 100%)",
              padding: "10px 20px",
              borderBottom: "1px solid #e2e6ed",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 28, height: 28, borderRadius: "50%", background: "#5b6abf", color: "var(--text-on-brand)",
                  fontSize: 12, fontWeight: 700,
                }}>
                  {q.testQuestionNumber || idx + 1}
                </span>
                <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-strong)" }}>
                  Question {q.testQuestionNumber || idx + 1}
                </span>
              </div>
              {q.sectionName && (
                <span style={{
                  fontSize: 11, fontWeight: 600, color: "var(--text-muted)", background: "var(--surface-sunken)",
                  padding: "2px 10px", borderRadius: 12,
                }}>
                  {q.sectionName}
                </span>
              )}
            </div>

            <div style={{ padding: "16px 20px" }}>
              {/* Question text */}
              <div style={{
                marginBottom: 16, fontSize: 14, lineHeight: 1.65, color: "var(--text-strong)",
                paddingLeft: 4,
              }}
                dangerouslySetInnerHTML={{ __html: q.stemStatement || q.questionName || "(No question text)" }}
              />

              {/* Choices */}
              {q.choices.length > 0 && (
                <div style={{ marginBottom: 16, paddingLeft: 2 }}>
                  {q.choices.map(c => {
                    const isCorrect = es?.correctChoices.has(c.choiceString);
                    return (
                      <label
                        key={c.choiceString}
                        htmlFor={`choice-${q.mappingId}-${c.choiceString}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 12px", marginBottom: 4, borderRadius: 8,
                          cursor: "pointer", transition: "background 0.15s",
                          background: isCorrect ? "#f0fdf4" : "transparent",
                          border: isCorrect ? "1px solid #86efac" : "1px solid transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          id={`choice-${q.mappingId}-${c.choiceString}`}
                          checked={isCorrect || false}
                          onChange={() => toggleChoice(q.mappingId, c.choiceString)}
                          style={{
                            width: 18, height: 18, accentColor: "var(--status-success)",
                            cursor: "pointer", flexShrink: 0,
                          }}
                        />
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                          background: isCorrect ? "#22c55e" : "#e2e8f0",
                          color: isCorrect ? "#fff" : "#64748b",
                          fontSize: 12, fontWeight: 700,
                        }}>
                          {c.choiceString}
                        </span>
                        <span style={{ fontSize: 14, color: "var(--text-strong)", lineHeight: 1.5 }}
                          dangerouslySetInnerHTML={{ __html: c.name }}
                        />
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Answer key for Integer (4) / Integer Range (5) — no choices to tick */}
              {q.questionCategory === 4 && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
                  padding: "10px 14px", background: "var(--status-success-bg)", borderRadius: 8,
                  border: "1px solid #86efac",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--status-success-text)" }}>Correct Answer</span>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. 42"
                    style={{
                      width: 160, borderRadius: 6, border: "1px solid #86efac",
                      fontSize: 13, fontWeight: 600,
                    }}
                    value={es?.answer ?? ""}
                    onChange={e => setField(q.mappingId, "answer", e.target.value)}
                  />
                  <span style={{ fontSize: 12, color: "var(--status-success-text)" }}>
                    (comma-separate multiple accepted values)
                  </span>
                </div>
              )}
              {q.questionCategory === 5 && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
                  padding: "10px 14px", background: "var(--status-success-bg)", borderRadius: 8,
                  border: "1px solid #86efac",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--status-success-text)" }}>Answer Range</span>
                  <Form.Control
                    type="number"
                    size="sm"
                    placeholder="From"
                    style={{
                      width: 100, borderRadius: 6, border: "1px solid #86efac",
                      fontSize: 13, fontWeight: 600, textAlign: "center",
                    }}
                    value={es?.answerRangeStart ?? ""}
                    onChange={e => setField(q.mappingId, "answerRangeStart", e.target.value)}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--status-success-text)" }}>—</span>
                  <Form.Control
                    type="number"
                    size="sm"
                    placeholder="To"
                    style={{
                      width: 100, borderRadius: 6, border: "1px solid #86efac",
                      fontSize: 13, fontWeight: 600, textAlign: "center",
                    }}
                    value={es?.answerRangeEnd ?? ""}
                    onChange={e => setField(q.mappingId, "answerRangeEnd", e.target.value)}
                  />
                  <span style={{ fontSize: 12, color: "var(--status-success-text)" }}>
                    (student value counted correct when within this range, inclusive)
                  </span>
                </div>
              )}

              {/* Marks controls row */}
              <div style={{
                display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
                padding: "12px 16px", background: "var(--surface-page)", borderRadius: 8,
                border: "1px solid var(--border-default)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>Marks</span>
                  <Form.Control
                    type="number"
                    size="sm"
                    style={{
                      width: 72, borderRadius: 6, border: "1px solid var(--border-strong)",
                      fontSize: 13, fontWeight: 500, textAlign: "center",
                    }}
                    value={es?.marks ?? ""}
                    onChange={e => setField(q.mappingId, "marks", e.target.value)}
                  />
                </div>
                <div style={{ width: 1, height: 24, background: "var(--surface-sunken)" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>Negative Marks</span>
                  <Form.Control
                    type="number"
                    size="sm"
                    style={{
                      width: 72, borderRadius: 6, border: "1px solid var(--border-strong)",
                      fontSize: 13, fontWeight: 500, textAlign: "center",
                    }}
                    value={es?.negativeMarks ?? ""}
                    onChange={e => setField(q.mappingId, "negativeMarks", e.target.value)}
                  />
                </div>
                {supportsMarkingScheme(q) && (
                  <>
                    <div style={{ width: 1, height: 24, background: "var(--surface-sunken)" }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>Marking Scheme</span>
                      <Form.Select
                        size="sm"
                        style={{
                          width: "auto", borderRadius: 6, border: "1px solid var(--border-strong)",
                          fontSize: 13, fontWeight: 500, height: 31,
                        }}
                        value={es?.markingScheme ?? "-1"}
                        onChange={e => setField(q.mappingId, "markingScheme", e.target.value)}
                      >
                        {MARKING_SCHEME_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </>
                )}
                <div style={{ width: 1, height: 24, background: "var(--surface-sunken)" }} />
                <label
                  htmlFor={`bonus-${q.mappingId}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>Bonus Marks</span>
                  <input
                    type="checkbox"
                    id={`bonus-${q.mappingId}`}
                    checked={es?.bonusMark || false}
                    onChange={() => toggleBonus(q.mappingId)}
                    style={{ width: 16, height: 16, accentColor: "#5b6abf", cursor: "pointer" }}
                  />
                </label>
                <div style={{ marginLeft: "auto" }}>
                  <Button
                    size="sm"
                    onClick={() => handleUpdate(q)}
                    disabled={isSaving}
                    style={{
                      backgroundColor: "#5b6abf", color: "var(--text-on-brand)", border: "none",
                      borderRadius: 6, padding: "6px 20px", fontSize: 13, fontWeight: 600,
                      boxShadow: "0 2px 6px rgba(91,106,191,0.25)",
                      margin: "2px",
                    }}
                  >
                    {isSaving ? <><Spinner role="status" animation="border" size="sm" style={{ marginRight: 6 }} />Saving...</> : "Update"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {data.questions.length === 0 && (
        <div style={{
          textAlign: "center", padding: 48, color: "var(--text-faint)", fontSize: 15, fontWeight: 500,
          background: "var(--surface-card)", borderRadius: 10, border: "1px dashed var(--border-default)",
        }}>
          No questions found for this test.
        </div>
      )}
    </div>
  );
};

export default TestQuestionsPanel;
